import { Component, OnInit, OnDestroy } from '@angular/core';
import { LoadingController, ModalController, NavController, Platform } from '@ionic/angular';
import { Storage } from '@ionic/storage';
import {
  DashboardService as CmbDashboardApi,
  PositionsService as CmbPositionsApi,
  JobsService as CmbJobsApi,
  DashboardFeedItem,
  DashboardFeedItemJobApplication,
  JobApplication,
  LegalAcceptanceId,
  PositionUpdatesResponse,
  UserStrikes,
} from '@bluecrew/crew-member-backend-angular-client';
import { User } from '../models/user';
import { Observable, Subscription, of, throwError } from 'rxjs';
import { shareReplay, map, filter, catchError, switchMap, retryWhen, scan, delay } from 'rxjs/operators';
import { first, isEmpty, noop, identity } from 'lodash-es';
import { UserService } from '../services/user.service';
import { PubSubService } from '../services/pub-sub.service';
import { SegmentCMOSService } from '../services/segment-cmos.service';
import { Logger } from '../util/logger/logger.service';
import { presentLoader, dismissLoaderObserver } from '../util/ion-util';
import { getLegacyTerminalMessage } from '../util/terminal-message-util';
import { PostSubmitPage } from '../application-home/post-submit/post-submit.page';
import { PendingJobConfirmationsService } from '../services/pending-job-confirmations.service';
import { NotificationService } from '../services/notification.service';
import { AppRoutingUtil } from '../util/app-routing-util';
import { LAST_POSITION_UPDATE_DISMISSAL_TIMESTAMP_KEY, LAST_TERMS_OF_USE_CHECK_TIME_KEY, POSITION_UPDATE_TAKEOVER_TTL_MILLIS, TERMS_OF_USE_TTL_MILLIS } from 'src/app/util/storage-util';
import { CachedPolicyStrikeClientService } from '../services/cache-policy-strike-client.service';
import { SplitService } from '../services/split.service';
import { DASHBOARD_STRIKE_CHECK } from '../util/split-util';
import { AnalyticsPlugin } from '@plugins/analytics/analytics-plugin';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.page.html',
  styleUrls: ['./dashboard.page.scss'],
})
export class DashboardPage implements OnInit, OnDestroy {

  user$: Observable<User>;
  applicationFeedItem$: Observable<DashboardFeedItemJobApplication>;
  mainAreaFeedItems$: Observable<DashboardFeedItem[]>;
  showNoJobsText: boolean;
  showRestrictedText: boolean;
  showFeed: boolean;
  hasPositionUpdates: boolean = false;
  userPending: boolean;

  // add today's date to the dashboard
  today: number = Date.now();

  private ionViewLifecycleSubs: Subscription;
  private loadDataSubs: Subscription;
  private isLoading: boolean;
  private feedItemTypeWhitelist: readonly string[] = [
    'DashboardFeedItemUpcomingJobs',
    'DashboardFeedItemTopJobs',
    'DashboardFeedItemCompleteProfile',
    'DashboardTemplateImageTextImage',
    'DashboardFeedItemJobApplication',
    'DashboardTemplateText',
    'DashboardFeedItemTodaysJob',
    'DashboardFeedItemImportantTasks',
  ];

  constructor(
    private readonly userService: UserService,
    private readonly cmbDashboardApi: CmbDashboardApi,
    private readonly cmbPositionsApi: CmbPositionsApi,
    private readonly cmbJobsApi: CmbJobsApi,
    private readonly loadingCtrl: LoadingController,
    private readonly pubsub: PubSubService,
    private readonly modalCtrl: ModalController,
    private readonly navCtrl: NavController,
    private readonly segmentCMOS: SegmentCMOSService,
    private readonly platform: Platform,
    private readonly pendingJobConfirmationService: PendingJobConfirmationsService,
    private readonly notificationService: NotificationService,
    private readonly cachedPolicyStrikeClientService: CachedPolicyStrikeClientService,
    private readonly splitService: SplitService,
    private readonly storage: Storage,
    private readonly logger: Logger) {
  }

  ngOnInit() {
    this.user$ = this.userService.user;
  }

  ngOnDestroy() {
  }

  async ionViewWillEnter() {
    await this.userService.ready();

    this.today = Date.now();
    this.ionViewLifecycleSubs = new Subscription();
    this.ionViewLifecycleSubs.add(this.pubsub.foreground.subscribe({
      next: () => {
        if (!this.isLoading) {
          this.loadData();
        }
      },
    }));
    this.loadData();
  }

  ionViewWillLeave() {
    if (this.ionViewLifecycleSubs) {
      this.ionViewLifecycleSubs.unsubscribe();
    }
    this.ionViewLifecycleSubs = undefined;
  }

  async loadData() {
    this.today = Date.now();
    this.showNoJobsText = false;
    this.showRestrictedText = false;
    this.showFeed = false;
    this.isLoading = true;
    this.userPending = false;

    let dashboardFeed$: Observable<DashboardFeedItem[]>, pendingJobConfirmations, positionUpdatesResponse;
    let unacknowledgedStrikes: UserStrikes;

    // Check Terms of Use status if last check was over a day ago, and show Terms of Use takeover if not acknowledged
    const lastToUCheck = await this.storage.get(LAST_TERMS_OF_USE_CHECK_TIME_KEY);
    if (!lastToUCheck || (Date.now() > lastToUCheck + TERMS_OF_USE_TTL_MILLIS)) {
      const termsOfUseAcceptance = await this.userService.getLatestLegalAcceptance(LegalAcceptanceId.TermsOfUse);
      await this.storage.set(LAST_TERMS_OF_USE_CHECK_TIME_KEY, Date.now());
      if (!termsOfUseAcceptance) {
        await this.navCtrl.navigateForward(AppRoutingUtil.TERMS_OF_USE);
      }
    }

    await Promise.allSettled([
      this.userService.getUpdatedUser().toPromise(),
      this.cmbDashboardApi.getDashboardFeed(),
      this.cmbPositionsApi.getPositionUpdates().toPromise(),
      this.pendingJobConfirmationService.getPendingConfirmations(true)
    ]).then(([
      _user,
      settledDashboardFeed$,
      settledPositionUpdates,
      settledPendingJobConfirmations
    ]) => {
      dashboardFeed$ = this.getValueIfFulfilled(settledDashboardFeed$);
      positionUpdatesResponse = this.getValueIfFulfilled(settledPositionUpdates);
      pendingJobConfirmations = this.getValueIfFulfilled(settledPendingJobConfirmations);
    }).catch(error => {
      this.logger.error(error);
    });

    if(await this.splitService.isFeatureEnabled(DASHBOARD_STRIKE_CHECK,false)) {
      await Promise.allSettled([
        this.cachedPolicyStrikeClientService.getUnacknowledgedStrikes()
      ]).then(([
        settledUnacknowledgedStrikes,
      ]) => {
        unacknowledgedStrikes = this.getValueIfFulfilled(settledUnacknowledgedStrikes);
      });
    }

    if(unacknowledgedStrikes?.strikes?.length > 0) {
      await this.navCtrl.navigateForward(AppRoutingUtil.ACKNOWLEDGE_STRIKES, {
        state: {
          userStrikes: unacknowledgedStrikes
        }
      });
    }

    if (pendingJobConfirmations?.length > 0) {
      const { externalJobId, externalPingId, isWaitlistUpgrade } = pendingJobConfirmations[0];

      await this.navCtrl.navigateForward(`/confirm-job/${externalJobId}/${externalPingId}`, {
        state: {
          isWaitlistUpgrade
        }
      });
    }

    this.user$ = this.userService.user;
    if (this.loadDataSubs) {
      this.loadDataSubs.unsubscribe();
    }
    this.loadDataSubs = new Subscription();
    
    let userInfo = this.user$.pipe(
      switchMap(user => {
        if (!user || !user.userInfo) {
          return throwError(() => new Error('User or userInfo is null')); 
        }
        return of(user.userInfo); 
      }),
      retryWhen(errors =>
        errors.pipe(
          scan((retryCount, error) => {
            if (retryCount >= 3) {
              throw error; 
            }
            console.warn(`Retrying due to null userInfo... attempt #${retryCount + 1}`);
            return retryCount + 1;
          }, 0),
          delay(1000) 
        )
      ),
      catchError(err => {
        console.error('Error occurred after retries:', err);
        return of(null); 
      })
    );
    
    this.loadDataSubs.add(userInfo.subscribe(_ => {
      if (this.userService.isCurrentUserRestricted()) {
        this.showRestrictedText = true;
        this.userPending = this.userService.isCurrentUserPending();
        return;
      }
      this.showFeed = true;
    }));

    dashboardFeed$ = dashboardFeed$.pipe(
      catchError(err => {
        this.logger.error(err);
        this.segmentCMOS.addEvent('dashboardPage_loadFailure', {
          reason: err.message || 'Unknown'
        });
        // For now just default to returning an empty list when there's an error
        return of([] as DashboardFeedItem[]);
      }),
      map(items => items.filter(item => this.feedItemTypeWhitelist.includes(item._object_type))),
      shareReplay(1),
    );

    dashboardFeed$.subscribe(async items => {
      if (!items.some(item => item._object_type === 'DashboardFeedItemImportantTasks')) {
        this.hasPositionUpdates = positionUpdatesResponse?.positionUpdates?.length > 0;
        await this.showPositionUpdatesIfAvailable(positionUpdatesResponse);
      }
    });

    this.applicationFeedItem$ = dashboardFeed$.pipe(
      map(items => first(items.filter(item => item._object_type === 'DashboardFeedItemJobApplication')) as DashboardFeedItemJobApplication),
      filter(identity),
      filter(feedItemJobApp => {
        if (this.isJobAppInTerminalStatus(feedItemJobApp.jobApplication)) {
          if (!this.platform.is('mobileweb') && !this.platform.is('desktop')) {
            this.showPostSubmitModal(feedItemJobApp.jobApplication);
          }
          return false;
        }
        return true;
      }),
      shareReplay(1),  // showPostSubmitModal is a side-effect
    );

    this.mainAreaFeedItems$ = dashboardFeed$.pipe(
      map(items => items.filter(item => item._object_type !== 'DashboardFeedItemJobApplication')),
      map(items => items.length ? items : undefined),
    );

    const loader = await presentLoader(this.loadingCtrl);
    this.loadDataSubs.add(dashboardFeed$.subscribe(dismissLoaderObserver(loader)));
    this.loadDataSubs.add(dashboardFeed$.subscribe({
      next: feedItems => {
        this.isLoading = false;
        this.showNoJobsText = isEmpty(feedItems);

        // Log some metrics
        const itemTypeParams = {};
        feedItems?.forEach(item => {
          if (!itemTypeParams[item._object_type]) itemTypeParams[item._object_type] = 0;
          itemTypeParams[item._object_type]++;
        });
        AnalyticsPlugin.logEvent({
          name: 'dashboard_load_success',
          params: {
            ...itemTypeParams,
            itemCount: feedItems?.length || 0,
          },
        });
        // Only ask about notifications if we're reasonably sure we've internet and the dashboard
        // has already loaded
        this.notificationService.checkForPeriodicReminder();
      },
      error: err => {
        this.isLoading = false;

        // Log an error
        AnalyticsPlugin.logEvent({
          name: 'dashboard_load_error',
          params: {
            message: err?.message?.slice(0, 40) || '<none>',
          },
        });
      },
    }));
  }

  private async showPostSubmitModal(jobApp: JobApplication): Promise<void> {
    let terminalMessage;
    try {
      terminalMessage = await this.cmbJobsApi.getActiveJobApplicationTerminalMessage().toPromise() || getLegacyTerminalMessage(jobApp);
    } catch (err) {
      console.error(err);
      terminalMessage = getLegacyTerminalMessage(jobApp);
    }
    const modal = await this.modalCtrl.create({
      component: PostSubmitPage,
      cssClass: terminalMessage?.showPosition ? undefined : 'post-submit-bs',
      componentProps: {
        jobApp,
        terminalMessage,
      },
    });
    await modal.present();
    await this.cmbJobsApi.withdrawActiveJobApplication().toPromise().catch(noop);
    const { data: shouldGoToBrowseJobsPage } = await modal.onWillDismiss<boolean>();
    if (shouldGoToBrowseJobsPage) {
      await this.navCtrl.navigateForward(AppRoutingUtil.BROWSE_JOBS);
    } else {
      await this.navCtrl.navigateForward(AppRoutingUtil.SCHEDULE);
    }
  }

  private isJobAppInTerminalStatus(jobApp: JobApplication): boolean {
    return jobApp.acceptanceStatus && jobApp.acceptanceStatus !== 'pendingOnboardingRequirements';
  }

  private async showPositionUpdatesIfAvailable(positionUpdatesResponse: PositionUpdatesResponse) {
    if (this.hasPositionUpdates) {
      const lastTakeover = await this.storage.get(LAST_POSITION_UPDATE_DISMISSAL_TIMESTAMP_KEY)
      if (!lastTakeover || (Date.now() > lastTakeover + POSITION_UPDATE_TAKEOVER_TTL_MILLIS)) {
        await this.navCtrl.navigateForward(AppRoutingUtil.POSITION_UPDATES, {
          state: {
            positionUpdatesResponse: positionUpdatesResponse
          }
        });
      }
    }
  }

  async updateProfilePic() {
    await this.navCtrl.navigateForward(AppRoutingUtil.ACCOUNT_VIEW_VERIFICATION_CODE);
  }

  private getValueIfFulfilled<T>(promiseSettledResult: PromiseSettledResult<T>): T | null {
    return promiseSettledResult.status === 'fulfilled' ? promiseSettledResult.value : null;
  }
}
